import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";

export const createTaskAsync = createAsyncThunk(
  "tasks/createTaskAsync",
  async (taskData, {rejectWithValue}) => {
    try{
      if(taskData.statusId != 1) {
        delete taskData.completionCode;
        delete taskData.completionNotes;
        delete taskData.completionPartsSummary;
      }
      delete taskData.recoveryDestinationInput;

      if(taskData.caseId == "" || taskData.caseId == null) {
        delete taskData.caseId;
      }

      if(!taskData.recoveryDestination || !taskData.recoveryDestination.lat) {
        delete taskData.recoveryDestination;
      }
      
      const response = await httpClient.post(`/tasks`, taskData);
      
      return {
        id: response.data.id,
        ...taskData,
        status: response.status,
        success: response.status == 200 ? true : false
      };

    } catch(e) {
      let message = '';
      if(e.response?.data?.errors?.[0]) {
        message = e.response?.data?.errors?.[0];
      } else {
        Object.keys(e.response.data).forEach(fieldName => {
          message += `${fieldName} - ${e.response.data[fieldName]}`
        });
      }
      return rejectWithValue({
        message
      });
    }
  } 
);

export const updateTaskStatusAsync = createAsyncThunk(
  "tasks/updateTaskStatusAsync",
  async (data, {rejectWithValue}) => {

    try{
      const postData = {
        "StatusCode": data.statusCode,
        "Comments": data.comments
      };

      if(data.statusCode == 'COMP'){
        postData.CompletionDetails = {
          "Code": data.completionCode,
          "Notes": data.completionNotes,
          "PartsSummary": data.completionPartsSummary
        }
      }
      const response = await httpClient.post(`/tasks/${data.id}/updateStatus`, {
        ...postData
      });
      
      return {
        success: response.data.success,
        message: response.data.message,
        taskId: data.id,
        statusId: data.statusId,
        status: response.status,
        impersonatedUser: data.impersonatedUser
      }
    } catch(e) {
      const errors = e.response?.data;
      const allMessages = Object.values(errors).flat().filter(m => typeof m === 'string');
      return rejectWithValue({
        message: allMessages.join("\n") || 'An error occured. Task status could not be changed.',
      });
    }
  } 
);

export const updateTaskAsync = createAsyncThunk(
  "tasks/updateTaskAsync",
  async (data, {rejectWithValue}) => {
    try{
      
      if(!data.recoveryDestination || !data.recoveryDestination?.lat) {
        delete data.recoveryDestination;
      }

      if(data.caseId == null || data.caseId == '') {
        data.caseId = data.id;
      }

      const response = await httpClient.put(`/tasks`, data);
      
      return {
        ...data,
        success: response.data.success,
        message: response.data.message,
        status: response.status
      };
    } catch(e) {
      let message = '';
      if(e.response?.data?.errors?.[0]) {
        message = e.response?.data?.errors?.[0];
      } else {
        Object.keys(e.response.data).forEach(fieldName => {
          message += `${fieldName} - ${e.response.data[fieldName]}`
        });
      }
      return rejectWithValue({
        message
      });
    }
  } 
);

export const assignPatrolAsync = createAsyncThunk(
  "tasks/assignPatrolAsync",
  async (data, {rejectWithValue}) => {
    try{
      const response = await httpClient.post(`/tasks/${data.id}/assign/`, {patrolId: data.patrolId});
      
      return {
        success: response.data.success,
        patrolId: data.patrolId,
        taskId: data.id,
        status: response.status,
      }
    } catch(e) {
      return rejectWithValue({
        message: e.response?.data?.errors?.[0],
      });
    }
  }
);

const tasksSlice = createSlice({
  name: "tasks",
  initialState: {
    loading: false,
    error: null,
    patrolId: 1,
    allTasks: [],
    filteredTasks: []
  },
  reducers: {
    setTasks: (state, action) => {
      //const tasks = action.payload.filter(t => t.statusId != 1);
      state.allTasks = action.payload
      state.filteredTasks = action.payload;
    },
    setPatrolId: (state, action) => {
        state.patrolId = action.payload
      },
    updateTask: (state, action) => {
      const index = state.allTasks.findIndex((task) => task.id === action.payload.id);
      if(index !== -1 && action.payload.statusId == 1) {
        
        state.allTasks = state.allTasks.filter(t => t.id != action.payload.id);
        state.filteredTasks = state.filteredTasks.filter(t => t.id != action.payload.id);
      } else {

        delete action.payload.createdOn;
        if (index !== -1) {
          state.allTasks[index] = { ...state.allTasks[index], ...action.payload };
        }
        const filteredIndex = state.filteredTasks.findIndex((task) => task.id === action.payload.id);
        if (index !== -1) {
          state.filteredTasks[filteredIndex] = { ...state.filteredTasks[filteredIndex], ...action.payload };
        }
      }
    },
    updateTaskStatus: (state, action) => {
      const { taskId, status } = action.payload;
      const task = state.allTasks.find((t) => t.id == taskId);
      if (task) {
        task.status = status
      }

      const filteredTask = state.filteredTasks.find(t => t.id === taskId);
      if(filteredTask){
        filteredTask.status = status;
      }
    },
    filterTasks: (state, action) => {
      const filters = action.payload.tasks;
      
      let tasks = state.allTasks;
      if(action.payload.mixed && action.payload.mixed.resource.length) {
        const mixedFilters = action.payload.mixed.resource;
        if(mixedFilters.length) {
          tasks = tasks.filter(t => 
            (mixedFilters.includes(0) && t.typeId == 1 && !t.assignedGarageId) ||
            (mixedFilters.includes(1) && t.typeId == 2 && !t.assignedGarageId) ||
            (mixedFilters.includes(2) && t.assignedGarageId) ||
            (mixedFilters.includes(3) && !t.assignedGarageId && !t.assignedPatrolId)
          );
        }
      }

      const comparisonMap = {
        vehicleRegistrationNumber: (taskVal, filterVal) => {

          return taskVal?.toLowerCase().startsWith(filterVal?.toLowerCase())
        },
        statusId: (taskVal, filterVal) => {
          if(filterVal.length) {

            return filterVal.includes(taskVal);
          } else {
            return true;
          }
        },
        default: (taskVal, filterVal) => 
          taskVal == filterVal
      }
      state.filteredTasks = tasks.filter(task =>
        Object.entries(filters).every( ([key, value]) => {
          if(value === undefined) return true;
          
          const compare = comparisonMap[key] || comparisonMap.default;
          return compare(task[key], value);
        })
      );
      //console.log('INITIAL FILTER TASKS', state.filteredTasks.length);
    },
    showAssignments: (state, action) => {
      
      const { id } = action.payload;
     
      const task = state.filteredTasks.find((t) => t.id == id);
      if (task) {
        task.showLines = task.showLines ? 0 : 1;
      }
    },
    updateStatus: (state, action) => {
      const { entityId, value } = action.payload;
      const task = state.allTasks.find((t) => t.id == entityId);
      const ftask = state.filteredTasks.find((t) => t.id == entityId);
      if (task) {
        task.status = value;
      }
      if (ftask) {
        ftask.status = value;
      }
    },
    updateLocation: (state, action) => {
      const { entityId, value } = action.payload;
      const task = state.allTasks.find((t) => t.id == entityId);
      const ftask = state.filteredTasks.find((t) => t.id == entityId);
      if (task) {
        task.location.lat = value.lat;
        task.location.lng = value.lng;
      }
      if (ftask) {
        ftask.location.lat = value.lat;
        ftask.location.lng = value.lng;
      }
    },
    batchUpdate: (state, action) => {
      console.log('BATCH UPDATE', action.payload);
      const updatesById = new Map();
      for (const update of action.payload) {
        updatesById.set(update.id, update);
      }
     
      state.allTasks = state.allTasks.map(t =>
        updatesById.has(t.id) && !t.localLock ? { ...t, ...updatesById.get(t.id) } : t
      ).filter(t => t.statusId != 1);
     
      state.filteredTasks = state.filteredTasks.map(t =>
        updatesById.has(t.id) && !t.localLock ? { ...t, ...updatesById.get(t.id) } : t
      ).filter(t => t.statusId != 1);
    },
    updateTaskWS: (state, action) => {
      const { id } = action.payload;
      const task = state.allTasks.find((t) => t.id == id);
      const ftask = state.filteredTasks.find((t) => t.id == id);
      if (task) {
        Object.assign(task, action.payload);
      }
      if (ftask) {
        Object.assign(ftask, action.payload);
      }
    },
    addTask: (state, action) => {
      const task = action.payload;
      state.allTasks.push(task);
      state.filteredTasks.push(task);
    },
    batchCreate: (state, action) => {
      console.log('BATCH CREATE', action.payload);
      state.allTasks.unshift(...action.payload);
      state.filteredTasks.unshift(...action.payload); // only if they belong in filtered view
    },
    assignPatrol: (state, action) => {
      const { value } = action.payload;
      const task = state.allTasks.find((t) => t.id == value.taskId);
      const ftask = state.filteredTasks.find((t) => t.id == value.taskId);
      if (task) {
        task.assignedPatrolId = value.patrolId;
      }
      if (ftask) {
        ftask.assignedPatrolId = value.patrolId;
      }
    },
    assignGarage: (state, action) => {
      const { taskId, garageId } = action.payload;
      const task = state.allTasks.find((t) => t.id == taskId);
      const ftask = state.filteredTasks.find((t) => t.id == taskId);
      if (task) {
        task.assignedGarageId = garageId;
      }
      if (ftask) {
        ftask.assignedGarageId = garageId;
      }
    },
    deassignGarage: (state, action) => {
      const { taskId } = action.payload;
      const task = state.allTasks.find((t) => t.id == taskId);
      const ftask = state.filteredTasks.find((t) => t.id == taskId);
      if (task) {
        task.assignedGarageId = 0;
      }
      if (ftask) {
        ftask.assignedGarageId = 0;
      }
    },
    lockTask: (state, action) => {
      const { taskId, userId } = action.payload;
      const task = state.allTasks.find((t) => t.id == taskId);
      const ftask = state.filteredTasks.find((t) => t.id == taskId);
      if (task) {
        task.localLock = true;
        task.lockedBy = userId;
      }
      if (ftask) {
        ftask.localLock = true;
        ftask.lockedBy = userId;
      }
    },
    unlockTask: (state, action) => {
      const { taskId } = action.payload;
      const task = state.allTasks.find((t) => t.id == taskId);
      const ftask = state.filteredTasks.find((t) => t.id == taskId);
      if (task) {
        task.localLock = false;
        delete task.lockedUserId;
        delete task.lockedUserName;
      }
      if (ftask) {
        ftask.localLock = false;
        delete ftask.lockedUserId;
        delete ftask.lockedUserName;
      }
    },
    addTaskComments: (state, action) => {
      const { comments, taskId } = action.payload;
      const index = state.allTasks.findIndex((task) => task.id === taskId);
      if (index !== -1) {
        state.allTasks[index] = { ...state.allTasks[index], comments };
      }

      const filteredIndex = state.filteredTasks.findIndex((task) => task.id === action.payload.id);
      if (index !== -1) {
        state.filteredTasks[filteredIndex] = { ...state.filteredTasks[filteredIndex], comments };
      }
    },
    testUpdate: (state, action) => {
      const { id,field, value } = action.payload;
      const index = state.allTasks.findIndex((task) => task.id === id);
      if (index !== -1) {
        state.allTasks[index][field] = value;
      }

      const filteredIndex = state.filteredTasks.findIndex((task) => task.id === action.payload.id);
      if (index !== -1) {
        state.filteredTasks[filteredIndex][field] = value;
      }
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(createTaskAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTaskAsync.fulfilled, (state) => {
        //state.data = action.payload;
        state.loading = false;

        // const newTask = action.payload;
        // if(newTask?.success) {
        //   console.log('NEW TASK FROM FULLFILLED', newTask);
        //   state.allTasks.push(newTask);
        //   state.filteredTasks.push(newTask);
        // }
      })
      .addCase(createTaskAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Task creation failed.';
      })


      .addCase(updateTaskStatusAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTaskStatusAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;

        // const resp = action.payload;
        // console.log('UPDATE STATUS FROM FULLFILLED', resp);
        // if (resp?.success) {
        //   console.log('REPLACE HERE', resp);
        //   const { status, taskId } = resp;
          
        //   const task = state.allTasks.find((t) => t.id == taskId);
        //   const ftask = state.filteredTasks.find((t) => t.id == taskId);
        //   console.log('CHANGE STATUS', status, task);

        //   if (task) {
        //     task.status = status;
        //     if(status == 1) {
        //       delete task.assignedPatrol;
        //     }
        //   }
        //   if (ftask) {
        //     ftask.status = status;
        //     if(status == 1) {
        //       delete ftask.assignedPatrol;
        //     }
        //   }
        // }
      })
      .addCase(updateTaskStatusAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Update status failed.';
        // console.log('CHECK STATE', JSON.stringify(state));
        // state.notification.open = true;
        // state.notification.severity = 'error';
        // state.notification.message = action.payload.response.data.errors?.join();
      })


      .addCase(assignPatrolAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(assignPatrolAsync.fulfilled, (state) => {
        state.loading = false;
        state.error = null;

        // const resp = action.payload;
        // console.log('ASSIGN TASK FROM FULLFILLED', resp);
        // if (resp?.success) {
        //   const {taskId, patrolCallSign} = resp;
        //   const task = state.allTasks.find((t) => t.id == taskId);
        //   const ftask = state.filteredTasks.find((t) => t.id == taskId);

        //   if (task) {
        //     task.assignedPatrol = patrolCallSign;
        //   }
        //   if (ftask) {
        //     ftask.assignedPatrol = patrolCallSign;
        //   }
          
        // }
        })
        .addCase(assignPatrolAsync.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload?.message || 'Update status failed.';
        })


        .addCase(updateTaskAsync.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(updateTaskAsync.fulfilled, (state) => {
          state.loading = false;
          state.error = null;

          // const resp = action.payload;
          // console.log('UPDATE TASK FROM FULLFILLED', resp);
          // if (resp?.success) {
          //   console.log('REPLACE HERE', resp);
          //   const { id } = resp;
          //   delete resp.success;
          //   const index = state.allTasks.findIndex((task) => task.id == id);
          //   if (index !== -1) {
          //     state.allTasks[index] = { ...state.allTasks[index], ...resp };
          //   }

          //   const filteredIndex = state.filteredTasks.findIndex((task) => task.id == id);
          //   if (index !== -1) {
          //     state.filteredTasks[filteredIndex] = { ...state.filteredTasks[filteredIndex], ...resp };
          //   }
          // }
        })
        .addCase(updateTaskAsync.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload?.message || 'Task status failed.';
        })
  }
});

export const { setTasks, updateTask, updateTaskStatus, setPatrolId } = tasksSlice.actions;
export default tasksSlice.reducer;
